import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '@core/auth/auth.service';

/**
 * Requests that must NOT carry the portal session token, and whose 401 must not
 * end the portal session.
 *
 * The playground calls the partner API with an OAuth access token it just
 * minted, and the public token endpoint with a client id/secret. Attaching the
 * portal bearer would overwrite the caller's own Authorization header, and
 * treating their 401 as a portal expiry would sign the developer out for what
 * is merely a bad client secret or an under-scoped token.
 *
 * The main app's customer API (/api/v4) is foreign too: a developer token is
 * never valid there, and the consent page signs a Zil Money customer in
 * through it while the developer may well be signed in to the portal.
 */
function isForeignAuthRequest(url: string): boolean {
  return (
    url.includes('/api/partner/') ||
    url.includes('/api/developer/oauth/token') ||
    url.includes('/api/v4/')
  );
}

/**
 * Attach Bearer token when present; on 401 clear session + bounce to /login.
 *
 * A request that already carries its own Authorization header — the consent
 * page acting as a Zil Money customer — keeps it, and its 401 is that caller's
 * to handle, not a portal sign-out.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const token = auth.token;
  const foreign = isForeignAuthRequest(req.url) || req.headers.has('Authorization');

  const authed =
    token && !foreign ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authed).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && token && !foreign) {
        auth.clear();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};
